/**
 * Separate page for signup-without-payment email verification.
 * Styled to match the shared verify-email pattern (light purple background, white card, 6-digit inputs).
 */
import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, TextField, Typography, CircularProgress } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthService from '../services/auth.service';
import { getAilaunchRedirectUrl } from '../utils/redirect.util';

export interface SignupVerifyEmailLocationState {
  email: string;
  password: string;
  companyId: string;
  companyName: string;
}

const CODE_LENGTH = 6;

const SignupVerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as SignupVerifyEmailLocationState | null;
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (!state?.email) {
      navigate('/signup-ai-flow', { replace: true });
    }
  }, [state, navigate]);

  const confirmationCode = digits.join('').trim();

  const handleDigitChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value.slice(-1);
    setDigits(newDigits);
    if (value && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    const newDigits = [...digits];
    pasted.split('').forEach((char, i) => { newDigits[i] = char; });
    setDigits(newDigits);
    const nextIndex = Math.min(pasted.length, CODE_LENGTH) - 1;
    inputRefs.current[nextIndex]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state || confirmationCode.length < CODE_LENGTH || isLoading) return;
    setIsLoading(true);
    try {
      await AuthService.confirmSignup({
        email: state.email,
        confirmationCode,
        password: state.password,
        companyId: state.companyId,
        companyName: state.companyName,
        infLeadSquaredId: '',
      });
      toast.success('Account verified.');
      window.location.href = getAilaunchRedirectUrl();
    } catch (err: any) {
      toast.error(err?.message ?? 'Invalid code or confirmation failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!state?.email || resendLoading) return;
    setResendLoading(true);
    try {
      await AuthService.resendConfirmationCode(state.email);
      toast.success('Confirmation code sent again. Check your email.');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to resend code.');
    } finally {
      setResendLoading(false);
    }
  };

  if (!state?.email) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F8F0FF',
        p: 1.5,
      }}
    >
      <Box sx={{ maxWidth: 480, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
        <Box sx={{ textAlign: 'center', width: '100%', mb: 1.5 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'inherit',
              fontSize: { xs: '1.75rem', sm: '2rem' },
              fontWeight: 700,
              color: '#1A1A1A',
              marginBottom: 0.5,
              lineHeight: 1.2,
            }}
          >
            Verify your email
          </Typography>
          <Typography
            sx={{
              fontFamily: 'inherit',
              fontSize: '1rem',
              fontWeight: 400,
              color: '#666666',
              lineHeight: 1.3,
            }}
          >
            We sent a code to {state.email}
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            maxWidth: 480,
            width: '100%',
            p: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            borderRadius: '24px',
            bgcolor: '#ffffff',
          }}
        >
          <Box
            display="flex"
            gap={1.5}
            justifyContent="center"
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            {digits.map((digit, index) => (
              <TextField
                key={index}
                inputRef={(el) => { inputRefs.current[index] = el; }}
                value={digit}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e as React.KeyboardEvent<HTMLInputElement>)}
                onPaste={handlePaste}
                inputProps={{
                  maxLength: 1,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  'aria-label': `Digit ${index + 1}`,
                }}
                disabled={isLoading}
                sx={{
                  width: 48,
                  '& .MuiOutlinedInput-root': {
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: '#fff',
                    '& fieldset': {
                      borderColor: '#CFC4E2',
                      borderWidth: 1,
                    },
                    '&:hover fieldset': { borderColor: '#9B51E0' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6F42C1',
                      borderWidth: 1,
                    },
                    '& input': {
                      padding: 0,
                      textAlign: 'center',
                      fontSize: '1.125rem',
                      fontWeight: 600,
                      color: '#1A1A1A',
                    },
                  },
                }}
              />
            ))}
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading || confirmationCode.length !== CODE_LENGTH}
            sx={{
              py: 1.25,
              borderRadius: 2,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              ...(confirmationCode.length !== CODE_LENGTH || isLoading
                ? { bgcolor: '#E0E0E0', color: '#A0A0A0', cursor: 'not-allowed' }
                : { bgcolor: '#6F42C1', color: '#fff', '&:hover': { bgcolor: '#5a32a3' } }
              )
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Continue'}
          </Button>

          <Typography
            component="button"
            type="button"
            onClick={handleResendCode}
            disabled={resendLoading || isLoading}
            sx={{
              display: 'block',
              width: '100%',
              mt: 1.5,
              textAlign: 'center',
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: '#6F42C1',
              textDecoration: 'underline',
              background: 'none',
              border: 'none',
              cursor: resendLoading || isLoading ? 'default' : 'pointer',
              '&:hover': resendLoading || isLoading ? {} : { color: '#5a32a3' },
              '&:disabled': { opacity: 0.7 },
            }}
          >
            {resendLoading ? 'Sending…' : 'Resend code'}
          </Typography>

          <Button
            type="button"
            fullWidth
            variant="outlined"
            sx={{
              mt: 1.5,
              py: 1,
              borderRadius: 2,
              borderColor: '#CFC4E2',
              color: '#666666',
              textTransform: 'none',
              fontSize: '0.875rem',
              '&:hover': { borderColor: '#6F42C1', color: '#6F42C1', bgcolor: 'rgba(111,66,193,0.05)' },
            }}
            onClick={() => navigate('/signup-ai-flow')}
            disabled={isLoading}
          >
            Back to form
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SignupVerifyEmail;
